import { useRef, useState } from 'react';
import { C } from '../theme/palette';
import { captureLePointeur, pointsDuMouvement, useBloqueLeDefilement } from './bloqueDefilement';

/**
 * Zone où l'on signe avec le doigt, en autant de traits qu'il faut.
 *
 * @param {object} props
 * @param {(image: string) => void} props.onSign reçoit la signature (PNG), ou `''` une fois effacée
 * @param {boolean} [props.aValider] la signature n'est transmise qu'au toucher de
 *   « Valider » : là où l'écran remplace la zone par la signature enregistrée,
 *   lever le doigt après le premier trait la refermait — on ne pouvait pas
 *   signer en plusieurs traits
 * @returns {JSX.Element} la zone de signature
 */
export function SignaturePad({ onSign, aValider = false }) {
  const cv = useRef(null);
  const drawing = useRef(false);
  const videRef = useRef(true);
  const [traceeAValider, setTraceeAValider] = useState(false);
  useBloqueLeDefilement(cv);
  const pos = (e) => {
    const c = cv.current;
    const r = c.getBoundingClientRect();
    const t = (e.touches && e.touches[0]) || e;
    return [
      (t.clientX - r.left) * (c.width / r.width),
      (t.clientY - r.top) * (c.height / r.height),
    ];
  };
  const start = (e) => {
    if (e.preventDefault) e.preventDefault();
    captureLePointeur(e);
    drawing.current = true;
    const [x, y] = pos(e);
    const ctx = cv.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    if (e.preventDefault) e.preventDefault();
    const ctx = cv.current.getContext('2d');
    pointsDuMouvement(e).forEach((p) => {
      const [x, y] = pos(p);
      ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#0F1A2E';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    videRef.current = false;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    // La référence, pas l'état : au premier trait, l'état disait encore
    // « vide » et la signature n'était pas transmise.
    if (videRef.current) return;
    if (aValider) setTraceeAValider(true);
    else if (onSign) onSign(cv.current.toDataURL('image/png'));
  };
  const valide = () => {
    if (!videRef.current && onSign) onSign(cv.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = cv.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    videRef.current = true;
    setTraceeAValider(false);
    if (!aValider && onSign) onSign('');
  };
  return (
    <div>
      <canvas
        ref={cv}
        width={640}
        height={200}
        style={{
          width: '100%',
          height: 140,
          border: '1.5px dashed ' + C.teal,
          borderRadius: 10,
          background: '#fff',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          display: 'block',
        }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 5,
        }}
      >
        <span style={{ fontSize: 11, color: C.muted }}>✍️ Signe ci-dessus avec le doigt</span>
        <button
          onClick={clear}
          style={{
            background: 'none',
            border: 'none',
            color: C.red,
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Effacer
        </button>
      </div>
      {aValider && (
        <button
          type="button"
          disabled={!traceeAValider}
          onClick={valide}
          style={{
            marginTop: 8,
            width: '100%',
            border: 'none',
            borderRadius: 9,
            padding: '10px',
            fontSize: 11,
            fontWeight: 800,
            cursor: traceeAValider ? 'pointer' : 'default',
            background: traceeAValider ? C.navy : C.border,
            color: '#fff',
          }}
        >
          ✓ Valider ma signature
        </button>
      )}
    </div>
  );
}
